import { type FC, useState } from "react";
import { Box, Card, Divider, Stack, Typography, useTheme } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { DateTime } from "luxon";
import { DataGridPro, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid-pro";
import { PageContainer, SearchTextField } from "@packsize/components";

import { type ClosedCarton } from "@/schemas";
import { BackButton } from "@/components/details-container";
import { useDebouncedSearch } from "@/hooks/use-debounced-search";
import { FailedRetry } from "@/components/failed-retry";

import { ClosingEmptyState } from "../awaiting-closing-container";
import { getClosingStatus } from "../utils";
import { useDownstreamMachineAliasLookup } from "../hooks/use-downstream-machine-alias-lookup";
import { useClosingHistory } from "../hooks/use-closing-history";

import { ClosingHistoryActionMenu } from "./closing-history-action-menu/closing-history-action-menu";

const HistoryNoRowsOverlay: FC = () => {
	const intl = useIntl();

	return (
		<ClosingEmptyState
			message={intl.formatMessage({
				id: "Nothing Closed Recently",
				defaultMessage: "Nothing Closed Recently",
			})}
		/>
	);
};

export const History: FC = () => {
	const intl = useIntl();
	const theme = useTheme();
	const [page, setPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(50);
	const { inputValue, filter, handleInputChange, handleSearch } = useDebouncedSearch({ emptyValue: "" });

	const {
		data: closingHistoryData,
		isLoading,
		error,
	} = useClosingHistory({
		pageNumber: page,
		pageSize: rowsPerPage,
		filter,
	});

	const { getMachineAlias } = useDownstreamMachineAliasLookup();

	const historyCartons = closingHistoryData?.items ?? [];
	const totalCount = closingHistoryData?.totalCount ?? 0;

	const handlePaginationModelChange = (newModel: GridPaginationModel) => {
		if (newModel.pageSize !== rowsPerPage) {
			setRowsPerPage(newModel.pageSize);
			setPage(1);
		} else {
			setPage(newModel.page + 1);
		}
	};

	const columns: GridColDef<ClosedCarton>[] = [
		{
			field: "closedDateUTC",
			headerName: intl.formatMessage({ id: "Closed Date", defaultMessage: "Closed Date" }),
			flex: 1,
			minWidth: 160,
			valueGetter: (_value, row) => DateTime.fromJSDate(row.closedDateUTC).toFormat("MM/dd/yy, HH:mm:ss"),
			disableColumnMenu: true,
			sortable: false,
		},
		{
			field: "alias",
			headerName: intl.formatMessage({ id: "Title", defaultMessage: "Title" }),
			flex: 1,
			minWidth: 150,
			disableColumnMenu: true,
			sortable: false,
		},
		{
			field: "identifier",
			headerName: intl.formatMessage({ id: "Barcode", defaultMessage: "Barcode" }),
			flex: 1,
			minWidth: 150,
			disableColumnMenu: true,
			sortable: false,
		},
		{
			field: "closingMachineId",
			headerName: intl.formatMessage({ id: "Machine", defaultMessage: "Machine" }),
			flex: 1,
			minWidth: 150,
			valueGetter: (_value, row) => getMachineAlias(row.closingMachineId),
			disableColumnMenu: true,
			sortable: false,
		},
		{
			field: "status",
			headerName: intl.formatMessage({ id: "Status", defaultMessage: "Status" }),
			flex: 1,
			minWidth: 120,
			valueGetter: (_value, row) => getClosingStatus(row.status, intl),
			disableColumnMenu: true,
			sortable: false,
		},
		{
			field: "closingId",
			headerName: "",
			width: 60,
			disableColumnMenu: true,
			sortable: false,
			headerAlign: "right",
			align: "right",
			renderCell: (params) => <ClosingHistoryActionMenu closedCarton={params.row} />,
		},
	];

	if (error) {
		return <FailedRetry retry={() => window.location.reload()} />;
	}

	return (
		<PageContainer sx={{ gap: 2 }}>
			<BackButton />
			<Card sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
				<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ padding: 2, gap: 2 }}>
					<Stack>
						<Typography variant="h5">
							<FormattedMessage id="History" defaultMessage="History" />
						</Typography>
						<Typography variant="caption">
							<FormattedMessage id="Last 7 Days" defaultMessage="Last 7 Days" />
						</Typography>
					</Stack>
					<SearchTextField
						size="small"
						value={inputValue}
						onChange={handleInputChange}
						placeholder={intl.formatMessage({
							id: "Title or Barcode...",
							defaultMessage: "Title or Barcode...",
						})}
						onSubmit={handleSearch}
						autoSelectOnFocus
					/>
				</Stack>

				<Divider sx={{ margin: 0 }} />
				<Box sx={{ width: "100%", flex: 1, minHeight: 0 }}>
					<DataGridPro
						rows={historyCartons}
						columns={columns}
						pagination
						paginationMode="server"
						paginationModel={{ page: page - 1, pageSize: rowsPerPage }}
						onPaginationModelChange={handlePaginationModelChange}
						rowCount={totalCount}
						pageSizeOptions={[50, 100, 200]}
						disableRowSelectionOnClick
						disableColumnSelector
						disableColumnResize
						getRowId={(row) => row.closingId}
						loading={isLoading}
						slots={{
							noRowsOverlay: HistoryNoRowsOverlay,
						}}
						sx={{
							border: "none",
							"& .MuiDataGrid-columnHeader:focus": {
								outline: "none",
							},
							"& .MuiDataGrid-columnHeader:focus-within": {
								outline: "none",
							},
							"& .MuiDataGrid-columnSeparator": {
								display: "none",
							},
							"& .MuiDataGrid-overlayWrapperInner": {
								minHeight: 240,
								padding: 4,
							},
							"& .MuiTablePagination-root": {
								overflow: "hidden",
							},
							"& .MuiDataGrid-row:nth-of-type(odd)": {
								backgroundColor: theme.palette.grey[100],
							},
						}}
					/>
				</Box>
			</Card>
		</PageContainer>
	);
};
